// Axios instance and types
import http from '../api'
import { APIResponse, Filters, APIPaginatedResponse, Pagination } from '../types'
import { TypeSale, TypeSaleItem } from '../../types/Sale'
import { getRequestParamString, parseDate } from '../utils/utils'
import { TypeInvoicePayment } from '../../types/Payment'
import { lineDiscountPayload } from '../../utils/lineDiscount'

const apiPath = import.meta.env.VITE_API_PATH

type SaleRequestPayload = {
  client_uuid: string | null
  date: string | null
  discount_type: number
  discount_value?: number
  discount_amount?: number
  tax_type: number
  tax_value?: number
  tax_amount?: number
  sale_items: Array<{
    item_uuid: string
    unit_uuid?: string | null
    service_point_id?: number | null
    staff_uuids: string[]
    unit_price: number
    quantity: number | undefined
    total_price: number | undefined
    discount_type?: number
    discount_value?: number
    discount_amount?: number
    comment?: string
  }>
  customer_name?: string | null
  customer_phone?: string | null
  customer_email?: string | null
  customer_tin?: string | null
  customer_address?: string | null
  loyalty_points?: number
  loyalty_amount?: number
  total_sale_amount: number
  total_sale_amount_payable: number
  comment?: string
}

const normalizeStaffUuids = (staffUuids: TypeSaleItem['staff_uuids']): string[] => {
  if (Array.isArray(staffUuids)) {
    return staffUuids
  }

  return staffUuids ? [staffUuids] : []
}

/** Empty string fails Laravel `uuid` validation; walk-ins must send null. */
const normalizeOptionalClientUuid = (clientUuid: TypeSale['client_uuid']): string | null => {
  return clientUuid ? clientUuid : null
}

const buildSalePayload = (sale: TypeSale): SaleRequestPayload => {
  const clientUuid = normalizeOptionalClientUuid(sale.client_uuid)
  const isWalkIn = !clientUuid

  return {
    client_uuid: clientUuid,
    date: sale.date ? parseDate('' + sale.date) : null,
    discount_type: sale.discount_type,
    discount_value: sale.discount_value,
    discount_amount: sale.discount_amount,
    tax_type: sale.tax_type,
    tax_value: sale.tax_value,
    tax_amount: sale.tax_amount,
    sale_items: sale.sale_items.map((item) => ({
      item_uuid: item.item_uuid,
      unit_uuid: item.unit_uuid ?? undefined,
      service_point_id: item.service_point_id,
      staff_uuids: normalizeStaffUuids(item.staff_uuids),
      unit_price: item.unit_price,
      quantity: item.quantity,
      total_price: item.total_price,
      comment: item.comment,
      ...lineDiscountPayload(item),
    })),
    customer_name: sale.customer_name ?? null,
    customer_phone: sale.customer_phone ?? null,
    customer_email: sale.customer_email ?? null,
    customer_tin: sale.customer_tin ?? null,
    customer_address: sale.customer_address ?? null,
    loyalty_points: isWalkIn ? 0 : sale.loyalty_points,
    loyalty_amount: isWalkIn ? 0 : sale.loyalty_amount,
    total_sale_amount: sale.total_sale_amount,
    total_sale_amount_payable: sale.total_sale_amount_payable,
    comment: sale.comment,
  }
}

async function getSales(filters?: Partial<Filters>, pagination?: Partial<Pagination>) {
  return await http.get<APIPaginatedResponse<any>>(apiPath + 'sales' + getRequestParamString(filters, pagination))
}

async function getSale(uuid: string) {
  return await http.get<APIResponse<any>>(`${apiPath}sales/${uuid}`)
}

async function getSaleByNumber(saleNumber: string) {
  return await http.get<APIResponse<any>>(`${apiPath}sales/number/${saleNumber}`)
}

async function create(sale: TypeSale, paymentData?: TypeInvoicePayment) {
  const payload: SaleRequestPayload & { payment_data?: TypeInvoicePayment } = buildSalePayload(sale)

  if (paymentData) {
    payload.payment_data = paymentData
  }

  return await http.post<APIResponse<any>>(apiPath + 'sales', payload, { errorHandle: false } as any)
}

async function update(uuid: string, sale: TypeSale) {
  const payload = buildSalePayload(sale)
  return await http.patch<APIResponse<any>>(`${apiPath}sales/${uuid}`, payload, { errorHandle: false } as any)
}

async function deleteSale(uuid: string) {
  return await http.delete<APIResponse<boolean>>(`${apiPath}sales/${uuid}`)
}

export default {
  getSales,
  getSale,
  getSaleByNumber,
  create,
  update,
  delete: deleteSale,
}
